import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ContratoItemModal({ show, contratoItem, contrato, onClose, onSave }) {
  const [formData, setFormData] = useState({
    descricao: '',
    marca: '',
    unidade: '',
    quantidade: 0,
    valorUnit: '',
  });
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backendError, setBackendError] = useState('');
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (show) {
      if (contratoItem) {
        setFormData({
          descricao: contratoItem.descricao || '',
          marca: contratoItem.marca || '',
          unidade: contratoItem.unidade || '',
          quantidade: contratoItem.quantidade || 0,
          valorUnit: contratoItem.valorUnit || '',
        });
      } else {
        setFormData({
          descricao: '',
          marca: '',
          unidade: '',
          quantidade: 0,
          valorUnit: '',
        });
      }
      setValidated(false);
      setBackendError('');
    }
  }, [show, contratoItem]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const calcularValorTotal = () => {
    const qtd = parseFloat(formData.quantidade) || 0;
    const vlrUnit = parseFloat(formData.valorUnit) || 0;
    return qtd * vlrUnit;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.descricao) {
      setValidated(true);
      return;
    }

    setLoading(true);
    setBackendError('');

    const payload = {
      ...formData,
      id: contratoItem?.id || null,
      idContrato: contrato.id,
      quantidade: parseFloat(formData.quantidade) || 0,
      valorUnit: parseFloat(formData.valorUnit) || 0,
    };

    try {
      if (contratoItem?.id) {
        await axios.put(`${API_BASE_URL.CONTRATO_ITEM}/${contrato.id}/${contratoItem.id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (onSave) {
          onSave();
        }
        if (onClose) {
          onClose();
        }
        showDefaultModal('success', 'Item atualizado com sucesso!');
      } else {
        await axios.post(API_BASE_URL.CONTRATO_ITEM, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (onSave) {
          onSave();
        }
        if (onClose) {
          onClose();
        }
        showDefaultModal('success', 'Item cadastrado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      const msg = error.response?.data?.error || 'Erro ao salvar item. Verifique os dados e tente novamente.';
      setBackendError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div
              className="modal-header text-white"
              style={{
                background:
                  'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
                padding: '0.5rem 1rem',
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                border: 'none',
              }}
            >
              <h6 className="modal-title mb-0">
                <i className="bi bi-list-ul me-2"></i>
                {contratoItem ? 'Editar Item' : 'Novo Item'}
              </h6>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={loading}
                title="Fechar"
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info mb-3 py-2">
                <small>
                  <i className="bi bi-info-circle me-2"></i>
                  Contrato: <strong>{contrato.numContrato}/{contrato.ano}</strong>
                  {contrato.fornecedor && (
                    <> - {contrato.fornecedor.nome}</>
                  )}
                </small>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {backendError && (
                  <div className="alert alert-danger py-2 mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>{backendError}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label small fw-semibold">
                    Descrição <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${validated && !formData.descricao ? 'is-invalid' : ''}`}
                    rows={3}
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    placeholder="Descrição do item..."
                    disabled={loading}
                  />
                  {validated && !formData.descricao && (
                    <div className="invalid-feedback">
                      Por favor, informe a descrição do item.
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Marca</label>
                      <input
                        type="text"
                        className="form-control"
                        name="marca"
                        value={formData.marca}
                        onChange={handleChange}
                        maxLength={100}
                        placeholder="Marca do produto"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Unidade</label>
                      <input
                        type="text"
                        className="form-control"
                        name="unidade"
                        value={formData.unidade}
                        onChange={handleChange}
                        maxLength={50}
                        placeholder="UN, KG, M, etc."
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Quantidade</label>
                      <input
                        type="number"
                        className="form-control"
                        name="quantidade"
                        value={formData.quantidade}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Valor Unitário</label>
                      <input
                        type="number"
                        className="form-control"
                        name="valorUnit"
                        value={formData.valorUnit}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Valor Total</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(calcularValorTotal())}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={loading}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-1"></i>
                        Salvar
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default ContratoItemModal;
